'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import { Container } from '@/components/Container';
import bg from '@/public/flow.webp';
import { backgroundImageMixin } from '@/styles/BackgroundimageMixin';
import { devices } from '@/styles/breakpoints';
import { TitleH2 } from '@/styles/typography';
import { IRoutePath } from '@/types/router';

const steps = [
  {
    value: 0,
    name: 'Work Orders',
    path: IRoutePath.FEATURES,
  },
  {
    value: 1,
    name: 'Schedule',
    path: IRoutePath.FEATURES_SCHEDULIG,
  },
  {
    value: 2,
    name: 'Complete Job',
    path: IRoutePath.FEATURES_ESTIMATES,
  },
  {
    value: 3,
    name: 'Invoice',
    path: IRoutePath.FEATURES_INVOICING,
  },
  {
    value: 4,
    name: 'Get Paid',
    path: IRoutePath.FEATURES,
  },
];
const stepsNum = {
  [IRoutePath.FEATURES]: {
    value: 0,
    name: 'Work Orders',
    path: IRoutePath.FEATURES,
  },
  [IRoutePath.FEATURES_SCHEDULIG]: {
    value: 1,
    name: 'Schedule',
    path: IRoutePath.FEATURES_SCHEDULIG,
  },
  [IRoutePath.FEATURES_ESTIMATES]: {
    value: 2,
    name: 'Complete Job',
    path: IRoutePath.FEATURES_ESTIMATES,
  },
  [IRoutePath.FEATURES_INVOICING]: {
    value: 3,
    name: 'Invoice',
    path: IRoutePath.FEATURES_INVOICING,
  },
  [IRoutePath.FEATURES_SALES]: {
    value: 4,
    name: 'Get Paid',
    path: IRoutePath.FEATURES,
  },
};

const Steper = ({ currentStep }) => {
  return (
    <SteperWrapper>
      {steps.map((step, index, arr) => {
        // const isActive = passed.includes(step.path);

        const isActive = currentStep >= step.value;

        const isCurrent = currentStep === step.value;
        return (
          <StepContent key={index}>
            <Link href={step.path}>
              <IconWrapper isCurrent={isCurrent} isActive={isActive}>
                <StepCount isCurrent={isCurrent} isActive={isActive}>
                  <span>{index + 1}</span>
                </StepCount>
                <StepLabel isCurrent={isCurrent} isActive={isActive}>
                  {step.name}
                </StepLabel>
              </IconWrapper>
            </Link>
            {index !== arr.length - 1 && <Line />}
          </StepContent>
        );
      })}
    </SteperWrapper>
  );
};

const LS = 'flow';

export const WorkFlowV2 = () => {
  const isBrowser = typeof window !== 'undefined';

  const [data, setData] = useState([]);
  const step =
    stepsNum[isBrowser ? window.location.pathname : IRoutePath.FEATURES];

  useEffect(() => {
    if (isBrowser) {
      const lc = JSON.parse(localStorage.getItem('flow'));
      if (!lc?.length) {
        setData([window.location.pathname]);
        localStorage.setItem(LS, JSON.stringify([window.location.pathname]));
      } else {
        const newData = [...new Set([...lc, window.location.pathname])];
        setData(newData);
        localStorage.setItem(LS, JSON.stringify(newData));
      }
    }
  }, [isBrowser]);

  return (
    <Wrapper>
      <Container>
        <WrapperInner>
          <Title>Success Job Steps</Title>
          <Steper currentStep={step?.value || 1} passed={data} />
        </WrapperInner>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${backgroundImageMixin(bg.src)}
  padding:16px 0;
`;

const WrapperInner = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  row-gap: 16px;
  @media only screen and ${devices.desktop} {
    flex-direction: row;
    align-items: center;
    gap: 40px;
    padding: 44px 0;
  }
`;

const Title = styled(TitleH2)`
  font-size: 24px;
  text-align: left;
  white-space: nowrap;
`;

const SteperWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: auto;
  width: 100%;
  padding-bottom: 16px;
  @media only screen and ${devices.desktop} {
    flex-direction: row;
    justify-content: center;
    padding: 0;
    margin-bottom: 0;
  }
`;

const Line = styled.div`
  height: 2px;
  width: 50.3px;
  background: rgba(32, 111, 237, 1);
  opacity: 15%;
`;

const StepContent = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  & a {
    text-decoration: none;
    display: block;
    width: 100%;
  }
`;
const IconWrapper = styled.div<{ isActive: boolean; isCurrent: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  background: ${(props) =>
    props.isCurrent
      ? 'rgba(32, 111, 237, 1)'
      : props.isActive
        ? 'rgb(210, 226, 251)'
        : 'white'};
  border-radius: 21px;
  padding: 4px;
  white-space: nowrap;

  @media only screen and ${devices.desktop} {
    padding: 10px 16px 10px 8px;
    gap: 10px;
  }
`;

const StepLabel = styled.div<{ isActive: boolean; isCurrent: boolean }>`
  font-family: var(--font-roboto);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;

  color: ${(props) =>
    props.isCurrent
      ? 'white'
      : props.isActive
        ? 'rgba(32, 111, 237, 1)'
        : 'rgba(77, 77, 77, 1)'};

  @media only screen and ${devices.desktop} {
    font-size: 14px;
  }
`;

const StepCount = styled.div<{ isActive: boolean; isCurrent: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(props) =>
    props.isCurrent
      ? 'rgba(255, 255, 255, 0.2)'
      : props.isActive
        ? 'rgba(255, 255, 255, 0.5)'
        : 'rgb(210, 226, 251)'};

  font-family: var(--font-roboto);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  color: ${(props) => (props.isCurrent ? 'white' : 'rgba(32, 111, 237, 1)')};
  @media only screen and ${devices.desktop} {
    font-size: 14px;
  }
`;
